/**
 * FR-070…085 · Phân quyền tên miền — route `/permissions?tab=by-user|by-domain`
 * (docs/08 §8.1). Bám bố cục `permissions.html`:
 *
 *   .page-head → .scope-note (chỉ User) → .card[data-tabs] → .tabs + .tabpanel
 *
 * Tab *Theo người dùng* chỉ hiện với `adm.users` (docs/05 §5.6);
 * tab *Theo tên miền* mở cho mọi vai trò, giới hạn ở tên miền họ đã thêm.
 */

import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import { Icon } from '../../components/Icon';
import { ByUserTab } from './ByUserTab';
import { ByDomainTab } from './ByDomainTab';

type TabId = 'by-user' | 'by-domain';

export function PermissionsPage() {
  const t = useT();
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();

  const isAdmin = can('adm.users');
  const requested = params.get('tab');
  const tab: TabId = requested === 'by-user' && isAdmin ? 'by-user' : 'by-domain';

  // Mở thẳng tab mặc định đúng vai trò khi chưa có tham số trên URL.
  useEffect(() => {
    if (!requested) {
      const next = new URLSearchParams(params);
      next.set('tab', isAdmin ? 'by-user' : 'by-domain');
      setParams(next, { replace: true });
    }
  }, [requested, isAdmin, params, setParams]);

  const active: TabId = requested ? tab : isAdmin ? 'by-user' : 'by-domain';

  const select = (next: TabId) => {
    const query = new URLSearchParams(params);
    query.set('tab', next);
    setParams(query);
  };

  return (
    <div data-od-id="page-permissions">
      <div className="page-head">
        <div className="grow">
          <h1 data-od-id="perm-title">{t('perm.title')}</h1>
          <p className="lede">{isAdmin ? t('perm.ledeAdmin') : t('perm.ledeUser')}</p>
        </div>
        {can('adm.domainroles') ? (
          <div className="row">
            <Link className="btn" to="/admin/domain-roles">
              {t('perm.manageDomainRoles')}
            </Link>
          </div>
        ) : null}
      </div>

      {!isAdmin ? (
        <div className="scope-note" style={{ marginBottom: 16 }} data-od-id="perm-user-scope">
          <Icon name="info" size={17} />
          <span>{t('perm.userScopeNote')}</span>
        </div>
      ) : null}

      <div className="card" data-od-id="perm-tabs">
        <div className="tabs" role="tablist">
          {isAdmin ? (
            <button
              type="button"
              role="tab"
              aria-selected={active === 'by-user'}
              onClick={() => select('by-user')}
            >
              {t('perm.tab.byUser')}
            </button>
          ) : null}
          <button
            type="button"
            role="tab"
            aria-selected={active === 'by-domain'}
            onClick={() => select('by-domain')}
          >
            {t('perm.tab.byDomain')}
          </button>
        </div>

        {isAdmin ? (
          <div className="tabpanel" hidden={active !== 'by-user'} data-od-id="panel-by-user">
            {active === 'by-user' ? <ByUserTab /> : null}
          </div>
        ) : null}

        <div className="tabpanel" hidden={active !== 'by-domain'} data-od-id="panel-by-domain">
          {active === 'by-domain' ? <ByDomainTab /> : null}
        </div>
      </div>
    </div>
  );
}
